import type { LatLng, LatLngBounds } from '@/types/global';
import type { ChargePointWrapper } from '../types/charge-point.types';
import { getCurrencyDisplay } from './price-utils';

export interface NearbyRow {
  wrapper: ChargePointWrapper;
  name: string;
  maxKw?: number;
  plug?: string;
  available: number;
  total: number;
  status: 'available' | 'busy' | 'offline';
  meters: number;
  priceLabel?: string;
}

/** Great-circle distance in meters */
export function distanceMeters(a: LatLng, b: LatLng): number {
  const r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r;
  const dLng = (b.lng - a.lng) * r;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371e3 * Math.asin(Math.sqrt(h));
}

const inBounds = ({ lat, lng }: LatLng, b: LatLngBounds) =>
  lat <= b.north && lat >= b.south && lng >= b.west && lng <= b.east;

/** "7.4 - 22 kW" -> 22 */
export function maxKwFromLabel(label?: string): number | undefined {
  const values = (label?.match(/[\d.]+/g) ?? []).map(Number);
  return values.length ? Math.max(...values) : undefined;
}

/**
 * Filled blocks for an availability bar of at most `max` blocks: one block
 * per charger, scaled down for large sites - but never 0 while any is free.
 */
export function filledBlocks(available: number, total: number, max: number) {
  if (total <= max) return available;
  const scaled = Math.round((available / total) * max);
  return available > 0 ? Math.max(1, scaled) : scaled;
}

// Intl formatters are costly to build; reuse one per locale/precision
const formatters = new Map<string, Intl.NumberFormat>();
function numberFormat(locale: string, min: number, max: number) {
  const key = `${locale}|${min}|${max}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    });
    formatters.set(key, f);
  }
  return f;
}

/**
 * Charge points and sites inside the viewport, nearest to the map center
 * first. The store keeps earlier viewports' pins (merge), hence the filter.
 */
export function buildNearbyRows(
  wrappers: ChargePointWrapper[],
  center: LatLng,
  bounds: LatLngBounds | null,
  limit = 60
): NearbyRow[] {
  return wrappers
    .filter((w) => !bounds || inBounds(w.coordinate, bounds))
    .map((w): NearbyRow => {
      const meters = distanceMeters(center, w.coordinate);
      const group = w.chargePointGroup;
      if (w.type === 'chargePointGroup' && group) {
        const colors = w.pin.colors ?? {};
        const total =
          group.chargePointCount ??
          Object.values(colors).reduce((sum, n) => sum + (n ?? 0), 0);
        const available = group.availableCount ?? colors.green ?? 0;
        return {
          wrapper: w,
          name: group.name,
          maxKw: maxKwFromLabel(group.maxKwLabel),
          plug: group.connectors?.[0]?.name ?? group.typeLabel,
          available,
          total: Math.max(total, available, 1),
          status:
            available > 0
              ? 'available'
              : (colors.orange ?? 0) > 0
                ? 'busy'
                : 'offline',
          meters,
          priceLabel: group.priceLabel,
        };
      }
      const cp = w.chargePoint;
      const state = cp?.chargePointState;
      return {
        wrapper: w,
        name: cp?.name ?? '',
        maxKw: cp?.maxKw,
        plug: cp?.connectors?.[0]?.name ?? cp?.typeLabel,
        available: state === 'available' ? 1 : 0,
        total: 1,
        status:
          state === 'available'
            ? 'available'
            : state === 'busy' || state === 'reserved'
              ? 'busy'
              : 'offline',
        meters,
        priceLabel: cp?.priceLabel,
      };
    })
    .sort((a, b) => a.meters - b.meters)
    .slice(0, limit);
}

/**
 * "1.10 DKK/kWh" -> { value: "1,10", unit: "kr/kWh" } (locale-aware).
 * Ranges show their lowest price; null for "Various"/unparseable labels.
 */
export function formatPriceLabel(
  label: string | undefined,
  locale: string
): { value: string; unit: string; free: boolean } | null {
  const [, amount, code, perKwh] =
    label?.match(/([\d.]+)(?:\s*-\s*[\d.]+)?\s*([A-Z]{3})(\/kWh)?/) ?? [];
  if (!amount || !code) return null;
  const value = numberFormat(locale, 2, 2).format(Number(amount));
  return {
    value,
    unit: getCurrencyDisplay({ identifier: code }) + (perKwh ?? ''),
    free: Number(amount) === 0,
  };
}

export function formatDistance(meters: number, locale: string): string {
  if (meters < 1000) return `${Math.round(meters / 10) * 10} m`;
  const km = meters / 1000;
  return `${numberFormat(locale, 0, km < 100 ? 1 : 0).format(km)} km`;
}
